import type { GraphQLAuditItem, GraphQLAuditDecision } from '@internal-audit/domain/graphql-types';
import { IconButton, Like, IconSize, OverlayMode } from '@soteria/design-system-ve';
import { useEffect, useState, useCallback } from 'react';

import { MessageToast } from '../../../core/components';
import { useUpsertAuditDecision } from '../../hooks/useUpsertAuditDecision';

export interface LikeIconButtonProps {
  /** The audit item data */
  item: GraphQLAuditItem;
  /** The ID of the audit session */
  sessionId: string;
  /** The current user's existing decision (if any) */
  currentUserDecision?: GraphQLAuditDecision;
  /** Optional callback when decision status changes */
  onDecisionSubmit?: (itemId: string, hasDecision: boolean) => void;
  /** Optional callback when skip status changes */
  onSkipToggle?: (itemId: string, isSkipped: boolean) => void;
  /** Optional callback when loading state changes */
  onLoadingChange?: (isLoading: boolean) => void;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Additional CSS class name */
  className?: string;
}

/**
 * A self-contained like icon button for "agree with all" audit decision functionality.
 * Uses the centralized useUpsertAuditDecision hook for consistent cache reactivity.
 */
// eslint-disable-next-line complexity
export function LikeIconButton({ item, sessionId, currentUserDecision, onDecisionSubmit, onLoadingChange, disabled = false, className }: LikeIconButtonProps) {
  // Use the provided currentUserDecision prop instead of trying to find it from item.decisions
  const hasCurrentUserDecision = !!currentUserDecision;

  const [isLoading, setIsLoading] = useState(false);
  const [dismissedErrorId, setDismissedErrorId] = useState<string | null>(null);

  // Use the centralized hook with comprehensive cache reactivity
  const {
    upsertAuditDecision,
    loading: mutationLoading,
    errors,
  } = useUpsertAuditDecision({
    item: { id: item.id, auditSessionId: sessionId },
    existingDecision: currentUserDecision,
  });

  // Get the first error for display (we'll show one toast at a time)
  const currentError = errors.length > 0 ? errors[0] : null;

  // Create a unique error ID based on error content to track dismissals
  const currentErrorId = currentError ? `${currentError.code}-${currentError.correlationId || 'no-correlation'}` : null;

  // Determine if we should show the error toast
  const shouldShowErrorToast = currentError && currentErrorId !== dismissedErrorId;

  // Combine local loading state with mutation loading
  const totalLoading = isLoading || mutationLoading;

  // Report loading state changes to parent
  useEffect(() => {
    onLoadingChange?.(totalLoading);
  }, [totalLoading, onLoadingChange]);

  // Handle toast dismissal - remember which error was dismissed
  const handleToastDismiss = useCallback(() => {
    if (currentErrorId) {
      setDismissedErrorId(currentErrorId);
    }
  }, [currentErrorId]);

  // Clear dismissed error ID when errors clear (new operation starts)
  useEffect(() => {
    if (errors.length === 0) {
      setDismissedErrorId(null);
    }
  }, [errors]);

  // Create decision handler
  const createDecision = useCallback(async () => {
    // Prepare the original labels from classification results
    const originalLabels =
      item.contentSnapshot?.classificationResults?.map(result => ({
        name: result.name,
        severity: result.severity,
      })) || [];

    // Create/update the decision using the simplified mutation structure - focus only on policies and labels
    return await upsertAuditDecision({
      auditItemId: item.id,
      auditSessionId: sessionId,
      originalLabels,
      labelsCorrect: true, // "Like" means labels are correct
      policiesCorrect: true, // "Like" means policies are correct
      // Optional fields for backward compatibility - don't set language/transcription
      existingDecisionId: currentUserDecision?.id,
      shouldDelete: false,
      correctedLabels: undefined, // Let BFF default to originalLabels — sending [] would be misinterpreted as "all labels are wrong"
      comments: null,
    });
  }, [item.id, item.contentSnapshot?.classificationResults, sessionId, currentUserDecision?.id, upsertAuditDecision]);

  const handleLikeToggle = useCallback(async () => {
    if (totalLoading) return;

    setIsLoading(true);
    const newDecisionState = true; // Always create/update a decision, never delete

    onDecisionSubmit?.(item.id, newDecisionState);

    try {
      const result = await createDecision();

      if (!result.success) {
        // Revert the optimistic update on failure
        onDecisionSubmit?.(item.id, hasCurrentUserDecision);
      }
    } catch {
      // Revert the optimistic update on error
      onDecisionSubmit?.(item.id, hasCurrentUserDecision);
    } finally {
      setIsLoading(false);
    }
  }, [totalLoading, item.id, hasCurrentUserDecision, onDecisionSubmit, createDecision]);

  // Handle click with blur to remove focus and prevent event bubbling
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation(); // Prevent card selection
    handleLikeToggle();
    // Remove focus from button after click
    event.currentTarget.blur();
  };

  return (
    <>
      {/* Show error toast if upsert failed and not dismissed */}
      {shouldShowErrorToast && currentError && (
        <MessageToast
          ariaLabel={`Upsert error: ${currentError.userMessage}`}
          isVisible={shouldShowErrorToast}
          message={currentError.userMessage}
          subMessage={`Error Code: ${currentError.code}${currentError.correlationId ? `\nCorrelation ID: ${currentError.correlationId}` : ''}`}
          variant="error"
          onClose={handleToastDismiss}
        />
      )}

      <IconButton
        aria-label="Agree with labels and policy"
        className={className}
        disabled={disabled || totalLoading}
        icon={Like}
        iconSize={IconSize.XSmall}
        overlayMode={OverlayMode.Solid}
        title="Agree with labels and policy"
        onClick={handleClick}
      />
    </>
  );
}
